import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  CellDoubleClickedEvent,
  ColDef,
  ColGroupDef,
  ColumnApi,
  ColumnRowGroupChangedEvent,
  ColumnState,
  DisplayedColumnsChangedEvent,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IAggFuncParams,
  ICellEditorParams,
  IDatasource,
  IsColumnFuncParams,
  MenuItemDef,
  PaginationChangedEvent,
  RowNode,
  RowNodeTransaction,
  SortChangedEvent,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import {
  arr_count,
  arr_filter,
  arr_reduce,
  arr_wrap,
  blank,
  data_get,
  data_has,
  data_is_true,
  data_nullify,
  data_omit,
  fallback,
  filled,
  Identifiable,
  is_callable,
  is_string,
  OptionalValue,
  spf,
  str_snake_case,
  Stringable,
  transform,
  when,
  whenFilled,
} from '../../utils/common';
import {BehaviorSubject, defer, of} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {
  FieldmagicDataGridCheckboxCellRenderer,
  FieldmagicDataGridCurrencyCellRenderer,
  FieldmagicDataGridDateTimeCellRenderer,
  FieldmagicDataGridTextCellRendererComponent,
  FieldmagicDataGridNumericCellRenderer,
  FieldmagicDataGridPercentageCellRenderer,
  FieldmagicDataGridProgressivePercentageCellRenderer,
  FieldmagicDataGridRenderingConfig,
  FieldmagicDataGridGroupCellRendererComponent,
} from './data-grid-cells-renderer.component';
import {ceil_percentage, multiply, to_numeric} from '../../utils/numbers';
import {
  FieldmagicDataGridCheckboxCellInputComponent,
  FieldmagicDataGridDateTimeCellInputComponent,
  FieldmagicDataGridDropdownCellInputComponent,
  FieldmagicDataGridInputConfig,
  FieldmagicDataGridRelateDropdownCellInputComponent,
  FieldmagicDataGridTextareaCellInputComponent,
} from './data-grid-inputs.component';
import {NotificationService} from '../../../services/notification.service';
import {filter, map, take, tap} from 'rxjs/operators';
import {LocalStorageService} from '../../../services/local-storage.service';
import {ClientStoreService} from '../../../services/client-store.service';
import {FieldmagicDataGridSidebarMenuToolPanelComponent} from './data-grid-accessories.component';
import {
  DataStateStore,
  FieldmagicDataStoreKey,
} from '../../store/data-state.store';

@Component({
  selector: `fieldmagic-data-grid`,
  template: `
    <ng-container *ngIf="options$ | async as options">
      <ag-grid-angular
        class="ag-theme-alpine fieldmagic-data-grid fieldmagic-data-grid-theme"
        [class.fieldmagic-data-grid-full]="size === 'full'"
        [class.fieldmagic-data-grid-lg]="size === 'lg'"
        [class.fieldmagic-data-grid-md]="size === 'md'"
        [rowData]="data$ | async"
        [columnDefs]="columns$ | async"
        [autoGroupColumnDef]="group$ | async"
        [frameworkComponents]="options.frameworkComponents"
        [rowGroupPanelShow]="options.rowGroupPanelShow"
        [getContextMenuItems]="options.getContextMenuItems"
        [getRowNodeId]="options.getRowNodeId"
        [rowGroupPanelShow]="options.rowGroupPanelShow"
        [defaultColDef]="{
          fullWidth: true,
          resizable: true
        }"
        [suppressAggFuncInHeader]="options.suppressAggFuncInHeader"
        [suppressDragLeaveHidesColumns]="true"
        [suppressMakeColumnVisibleAfterUnGroup]="true"
        [enableGroupEdit]="true"
        [rowSelection]="options.rowSelection"
        [isRowSelectable]="options.isRowSelectable"
        [enableCharts]="options.enableCharts"
        [enableRangeSelection]="options.enableRangeSelection"
        [suppressRowClickSelection]="true"
        [suppressRowDeselection]="true"
        [groupIncludeTotalFooter]="options.groupIncludeTotalFooter"
        [sideBar]="options.sideBar"
        [rowModelType]="options.rowModelType"
        [pagination]="options.pagination"
        [paginationPageSize]="options.paginationPageSize"
        [cacheBlockSize]="options.cacheBlockSize"
        [aggFuncs]="options.aggFuncs"
        (firstDataRendered)="onFirstDataRendered($event)"
        (gridReady)="onGridReady($event)"
        (columnRowGroupChanged)="options.onColumnRowGroupChanged($event)"
        (sortChanged)="onSortChanged($event)"
        (paginationChanged)="onPaginationChanged($event)"
        (cellValueChanged)="onCellChanged($event)"
      >
      </ag-grid-angular>
    </ng-container>
  `,
  styles: [
    `
      .fieldmagic-data-grid-full {
        height: 1024px;
      }
    `,
    `
      .fieldmagic-data-grid-lg {
        height: 720px;
      }
    `,
    `
      .fieldmagic-data-grid-md {
        height: 480px;
      }
    `,
  ],
})
export class FieldmagicDataGridComponent implements OnDestroy {
  @Input()
  set data(data: Identifiable[]) {
    this.data$.next(data);
  }

  @Input()
  set columns(values: FieldmagicDataGridColumnDef[]) {
    this.columns$.next(this._mapToGridDefs(values));
  }

  @Input()
  set options(value: FieldmagicDataGridOptions) {
    this.options$.next({
      ...this.options$.value,
      rowGroupPanelShow: data_is_true(value.enable_ui_grouping)
        ? 'always'
        : 'never',
      suppressAggFuncInHeader: fallback(value.disable_aggregates_on_header, {
        to: () => true,
      }),
      rowSelection: transform(value.enable_row_selection, {
        transformer: (value) =>
          when(value === 'multi', {
            then: () => 'multiple',
            else: () => 'single',
          }),
      }),
      enableCharts: fallback(value.enable_charts, {
        to: () => true,
      }),
      enableRangeSelection: fallback(value.enable_range_selection, {
        to: () => true,
      }),
      groupIncludeTotalFooter: fallback(value.enable_totals, {
        to: () => true,
      }),
      rowModelType: fallback(value.row_model_type, {
        to: () => 'clientSide',
      }),
      pagination: fallback(value.pagination, {
        to: () => false,
      }),
      paginationPageSize: fallback(value.pagination_page_size, {
        to: () => 10,
      }),
      cacheBlockSize: fallback(value.cache_block_size, {
        to: () => 100,
      }),
      ...(data_is_true(value.enable_configuring_columns) && {
          sideBar: {
            toolPanels: [
              {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
              },
              {
                id: 'fieldmagic:menu',
                labelDefault: 'Menu',
                labelKey: 'menus',
                iconKey: 'menu',
                toolPanel: '__sidebar:menu',
              },
            ],
            defaultToolPanel: 'columns',
          }
      }),
    });
  }

  @Input()
  set menus(values: FieldmagicDataGridContextMenu[]) {
    this._menus$.next(values);
  }

  @Input() size: FieldmagicDataGridSize = 'full';

  @Input()
  set fetching(value: boolean) {
    if (blank(this._grid)) {
      return;
    }

    if (value) {
      this._grid.showLoadingOverlay();
    } else {
      this._grid.hideOverlay();
    }
  }

  @Input() fit: boolean = false;

  @Input() storeKey?: OptionalValue<string>;

  @Input() dataStateKey?: OptionalValue<FieldmagicDataStoreKey>;

  @Output('onReady')
  $onReady = new EventEmitter<FieldmagicDataGridOnReadyEvent<Identifiable>>();

  @Output('onSort')
  $onSort = new EventEmitter<SortChangedEvent>();

  @Output('onPageChange')
  $onPageChange = new EventEmitter<PaginationChangedEvent>();

  readonly options$ = new BehaviorSubject<GridOptions>({
    getRowNodeId: (data: Identifiable) => data.id,
    frameworkComponents: {
      ..._COLUMN_TYPE_RENDERER_COMPONENTS,
      ..._COLUMN_TYPE_INPUT_COMPONENTS,
    },
    getContextMenuItems: this._getContextMenus.bind(this),
    onColumnRowGroupChanged: (event) => this._onRowGroupChanged(event),
    isRowSelectable: (node) => filled(node.data) && !node.group && !node.footer,
    suppressAggFuncInHeader: true,
    enableCharts: true,
    enableRangeSelection: true,
    groupIncludeTotalFooter: true,
    aggFuncs: _CUSTOM_AGGREGATES,
  });

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (blank(this.dataStateKey) || !this._data.has(this.dataStateKey)) {
      return;
    }

    const confirmed = confirm(this._translator.instant('confirm_cancel'));

    if (!confirmed) {
      event.preventDefault();
    }

    this._data.destroy(this.dataStateKey);
  }

  readonly columns$ = new BehaviorSubject<ColDef[]>([]);
  readonly data$ = new BehaviorSubject<Identifiable[]>([]);
  readonly group$ = new BehaviorSubject<OptionalValue<ColDef>>(null);

  private _grid?: OptionalValue<GridApi>;
  private _api?: OptionalValue<FieldmagicDataGridApi<Identifiable>>;

  private readonly _menus$ = new BehaviorSubject<
    FieldmagicDataGridContextMenu[]
  >([]);

  constructor(
    private readonly _translator: TranslateService,
    private readonly _notifications: NotificationService,
    private readonly _store: LocalStorageService,
    private readonly _client: ClientStoreService,
    private readonly _data: DataStateStore,
  ) {}

  ngOnDestroy(): void {
    if (filled(this._grid)) {
      this._grid.removeEventListener(
        'displayedColumnsChanged',
        this.onGridColumnsChanged.bind(this),
      );
    }
  }

  onGridReady(event: GridReadyEvent): void {
    this._grid = event.api;

    this.$onReady.emit({
      api: (this._api = new FieldmagicDataGridApi(
        event.api,
        this._notifications,
        this._data,
        {
          data_state_key: this.dataStateKey,
        },
      )),
    });

    /// WARNING: if enabled, this would disable the scrolling
    /// in the data grid which will force the columns to fit the entire size of the grid
    if (this.fit) {
      this._grid.sizeColumnsToFit();
    }

    this._grid.closeToolPanel();

    this._grid.addEventListener(
      'displayedColumnsChanged',
      this.onGridColumnsChanged.bind(this),
    );

    this._restoreColumnState(event.columnApi);
  }

  onSortChanged(event: SortChangedEvent): void {
    this.$onSort.emit(event);
  };

  onPaginationChanged(event: PaginationChangedEvent): void {
    this.$onPageChange.emit(event);
  };

  onFirstDataRendered(event: FirstDataRenderedEvent): void {
    event.api.forEachNode((node) => {
      if (node.group || blank(node.data)) {
        return;
      }

      if (filled(data_get(node.data, '__selected'))) {
        node.setSelected(data_is_true(node.data['__selected']));
      }
    });
  }

  onGridColumnsChanged(event: DisplayedColumnsChangedEvent): void {
    if (blank(this.storeKey)) {
      return;
    }

    const state = event.columnApi.getColumnState();
    const client = this._client.getActiveClient();

    if (blank(client)) {
      return;
    }

    const storeKey = spf('data-grid:%s:%s', {
      args: [client.client_id, this.storeKey],
    });

    this._store.setJsonItem(storeKey, state);
  }

  onCellChanged(): void {
    if (blank(this._api)) {
      return;
    }

    this._api.storeState();
  }

  private _getContextMenus(
    params: GetContextMenuItemsParams,
  ): (MenuItemDef | string)[] {
    const output: MenuItemDef[] = [];

    for (const definition of this._menus$.value) {
      if (filled(definition.predicate) && !definition.predicate(params)) {
        continue;
      }

      if (
        (data_is_true(definition.is_on_empty_node) && blank(params.node)) ||
        (data_is_true(definition.is_for_group_action) &&
          filled(params.node) &&
          params.node.group) ||
        (data_is_true(definition.is_row_action) &&
          filled(params.node) &&
          !params.node.group)
      ) {
        output.push({
          name: this._translator.instant(definition.label),
          action: () => definition.action(params),
        });
      }
    }

    const withSeparator =
      filled(output) && data_is_true(this.options$.value.enableCharts);

    return [
      ...output,
      ...(withSeparator && ['separator']),
      ...(data_is_true(this.options$.value.enableCharts) && ['chartRange']),
    ];
  }

  private _mapToGridDefs(columns: FieldmagicDataGridColumnDef[]): ColDef[] {
    const definitions: (ColDef | ColGroupDef)[] = [];

    let autoGroup: Record<string, any> = {};

    for (const idx in columns) {
      const column = columns[idx];

      const editable = fallback(column.editable, {
        to: () => true,
      });

      const label = fallback(column.label, {
        fallback: () =>
          spf('no_label_%d', {
            args: [idx],
          }),
      });

      const def: ColDef | ColGroupDef = {
        headerName: whenFilled(column.label, {
          then: () => this._translator.instant(label),
        }),
        field: fallback(column.field_name, {
          to: () =>
            str_snake_case(label, {
              lowercase: true,
            }),
        }),
        rowGroup: data_is_true(column.grouped),
        enableRowGroup: fallback(column.enable_grouping, {
          to: () => data_is_true(column.grouped),
        }),
        columnGroupShow: transform(column.hide_when_grouped_column_collapsed, {
          transformer: (value) => (value ? 'open' : 'closed'),
        }),
        keyCreator: transform(column.grouped || column.enable_grouping, {
          transformer: (_) => (params: _DataGridGroupIdGeneratorParams) => {
            if (filled(column.group_id)) {
              return column.group_id(data_get(params, 'value'), params);
            }

            const value = data_get(params, 'value');

            if (
              filled(value) &&
              (column.field_type === 'dropdown' ||
                column.field_type === 'relate')
            ) {
              return data_get(value, 'text');
            }

            return value;
          },
        }),
        hide: data_is_true(column.hidden),
        sortable: fallback(column.sortable, {
          to: () => true,
        }),
        filter: fallback(column.filterable, {
          to: () => true,
        }),
        pinned: column.pinned,
        checkboxSelection: (params) => {
          let enabled =
            filled(
              data_get(params, ['data', data_get(params, 'colDef.field')]),
            ) &&
            column.enable_checkbox &&
            !params.node.footer;

          if (is_callable(column.enable_checkbox)) {
            enabled =
              enabled &&
              (column.enable_checkbox as CallableFunction)(
                data_nullify(data_get(params, 'data')),
                params,
              );
          }

          return enabled;
        },
        editable: (params: IsColumnFuncParams) =>
          editable &&
          (!params.node.group ||
            data_is_true(
              data_get(
                (params.colDef.cellEditorParams as CallableFunction)(params),
                'enable_mass_update',
              ),
            )) &&
          transform(column.editable_predicate, {
            default_value: true,
            transformer: (fn) => fn(params),
          }) &&
          !params.node.footer,
        enableValue: editable,
        aggFunc: fallback(
          transform(column.aggregation, {
            transformer: (fn) => {
              if (is_string(fn)) {
                return fn;
              }

              return (params) => fn(params.values, params);
            },
          }),
          {
            to: () =>
              when<any>(column.field_type === 'checkbox', {
                then: () => 'checkbox_aggregate',
                else: () => 'sum',
              }),
          },
        ),
        allowedAggFuncs: fallback(column.enabled_aggregations, {
          to: () => [],
        }),
        children: transform(column.children, {
          transformer: (children) => this._mapToGridDefs(children),
        }),
        valueFormatter: (params) => {
          if (filled(column.formatter)) {
            return column.formatter(params.value, params);
          }

          const value = params.value;

          if (
            filled(value) &&
            (column.field_type === 'dropdown' || column.field_type === 'relate')
          ) {
            if (column.field_type === 'relate' && is_string(value)) {
              return value;
            }

            return data_get(value, 'text');
          }

          return value;
        },
        valueGetter: (params) => {
          if (params.node.group || blank(params.node.data)) {
            return;
          }

          if (is_callable(column.deserializer)) {
            return (column.deserializer as FieldmagicDataGridDeserializer)(
              params,
            );
          }

          return this._deserializeWithCommonMethods(params, {
            common_serializer:
              column.deserializer as FieldmagicDataGridCommonDeserializer,
            column,
          });
        },
        valueSetter: (params: ValueSetterParams) => {
          if (blank(column.serializer)) {
            params.data[params.colDef.field] = this._serializeValue(
              params.newValue,
              column,
            );
            return true;
          }

          const result = column.serializer(params.newValue, params);

          if (!result.success) {
            return false;
          }

          if (filled(result.updates)) {
            for (const key in result.updates) {
              params.data[key] = result.updates[key];
            }
          }

          return true;
        },
        cellRenderer: when(
          filled(column.field_type) &&
            data_has(_COLUMN_TYPE_RENDERER_COMPONENTS, column.field_type),
          {
            then: () => column.field_type,
            else: () => 'text',
          },
        ),
        cellEditor: transform(editable, {
          transformer: (value) => {
            if (!value || blank(column.field_type)) {
              return;
            }

            const alias = spf('input:%s', {
              args: [column.field_type],
            });

            if (blank(data_get(_COLUMN_TYPE_INPUT_COMPONENTS, alias))) {
              return;
            }

            return alias;
          },
        }),
        cellEditorParams: (params) => {
          if (!editable || blank(column.field_type)) {
            return;
          }

          let config = fallback(column.input_config, {
            to: () => ({}),
          });

          if (is_callable(config)) {
            config = {
              enable_mass_update: false,
              ...(config as CallableFunction)(params),
            };
          }

          return config;
        },
        suppressKeyboardEvent: transform(column.field_type === 'textarea', {
          transformer: (_) => (params) =>
            params.event.key === 'Enter' &&
            params.event.shiftKey &&
            params.editing,
        }),
        cellRendererParams: {
          ...(filled(column.rendering_config) && column.rendering_config),
          ...(data_is_true(column.is_group_child_count_ref) && {
            is_group_child_count_ref: true,
          }),
        },
        onCellDoubleClicked: when(
          !editable && is_callable(column.on_double_clicked),
          {
            then: () => (event) => column.on_double_clicked(event),
          },
        ),
        ...column.lib_column_def,
      };

      definitions.push(def);

      /// only one column can be autogrouped and non cell that has children
      if (column.auto_grouped && blank(autoGroup) && blank(def.children)) {
        autoGroup = {def, column, editable};
      }
    }

    if (filled(autoGroup)) {
      const {column, def, editable} = autoGroup;

      this.group$.next({
        headerName: def.headerName,
        initialPinned: column.pinned,
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: '__grouping',
        },
        editable: (params: IsColumnFuncParams) =>
          (def as ColDef).field === params.node.field &&
          editable &&
          !params.node.footer,
        cellEditor: (def as ColDef).cellEditor,
        cellEditorParams: (def as ColDef).cellEditorParams,
        valueSetter: transform(editable, {
          transformer: (_) => (params) => {
            if (!params.node.group) {
              return false;
            }

            for (const node of params.node.allLeafChildren) {
              if (blank(column.serializer)) {
                node.data[node.field] = this._serializeValue(
                  params.newValue,
                  column,
                );
                continue;
              }

              const result = column.serializer(params.newValue, {
                newValue: params.newValue,
                oldValue: params.oldValue,
                node,
                api: params.api,
                columnApi: params.columnApi,
                colDef: def as ColDef,
                data: node.data,
                column: params.column,
                context: params.context,
              });

              if (!result.success || blank(result.updates)) {
                return false;
              }

              for (const key in result.updates) {
                node.data[key] = result.updates[key];
              }
            }

            if (filled(this._grid)) {
              this._grid.refreshClientSideRowModel('group');
            }

            return true;
          },
        }),
      });
    }

    return definitions;
  }

  private _serializeValue(
    value: any,
    column: FieldmagicDataGridColumnDef,
  ): any {
    if (column.field_type === 'numeric' || column.field_type === 'currency') {
      return to_numeric(value);
    }

    if (column.field_type === 'checkbox') {
      return data_is_true(value);
    }

    if (
      column.field_type === 'percentage' ||
      column.field_type === 'progressive_percentage'
    ) {
      return ceil_percentage(value);
    }

    return value;
  }

  private _deserializeWithCommonMethods(
    params: ValueGetterParams,
    opts: {
      column: FieldmagicDataGridColumnDef;
      common_serializer?: FieldmagicDataGridCommonDeserializer;
    },
  ): any {
    if (params.node.group && opts.column.grouped) {
      return;
    }

    const data = params.data;

    if (
      filled(opts.common_serializer) &&
      filled(opts.common_serializer.multiply)
    ) {
      return multiply({
        multiplicant: data_get(
          data,
          opts.common_serializer.multiply.multiplicant_ref,
        ),
        multiplier: data_get(
          data,
          opts.common_serializer.multiply.multiplier_ref,
        ),
      });
    }

    if (
      opts.column.field_type === 'numeric' ||
      opts.column.field_type === 'currency' ||
      opts.column.field_type === 'percentage' ||
      opts.column.field_type === 'progressive_percentage'
    ) {
      return to_numeric(data_get(data, params.colDef.field));
    }

    if (opts.column.field_type === 'checkbox') {
      return data_is_true(data_get(data, params.colDef.field));
    }

    return data_get(data, params.colDef.field);
  }

  private _onRowGroupChanged(event: ColumnRowGroupChangedEvent): void {
    const columns = event.columnApi.getAllColumns();

    if (blank(columns)) {
      return;
    }

    for (const column of columns) {
      const def = column.getColDef();

      if (!def.rowGroup || !def.hide) {
        continue;
      }

      event.columnApi.setColumnsVisible([column], !column.isRowGroupActive());
    }
  }

  private _restoreColumnState(api: ColumnApi): void {
    if (blank(this.storeKey)) {
      return;
    }

    const client = this._client.getActiveClient();

    if (blank(client)) {
      return;
    }

    const storeKey = spf('data-grid:%s:%s', {
      args: [client.client_id, this.storeKey],
    });

    const stored: ColumnState[] = this._store.getJsonItem(storeKey);

    if (blank(stored)) {
      return;
    }

    const states: Record<string, ColumnState> = arr_reduce(
      api.getColumnState(),
      {
        initial_value: {},
        reducer: (initial, state) => ({
          ...initial,
          [state.colId]: state,
        }),
      },
    );

    const restorable: ColumnState[] = [];

    for (const _stored of stored) {
      const state = states[_stored.colId];

      if (blank(state)) {
        continue;
      }

      restorable.push({
        ..._stored,
        aggFunc: state.aggFunc,
      });
    }

    api.setColumnState(restorable);

    if (filled(this._grid)) {
      this._grid.recomputeAggregates();
    }
  }
}

export class FieldmagicDataGridApi<T extends Identifiable> {
  get data(): _IndexedData<T>[] {
    this._lib.stopEditing();

    const compiled: _IndexedData<T>[] = [];

    this._lib.forEachNode((node, index) => {
      if (blank(node.data)) {
        return;
      }

      compiled.push({
        ...(<T>data_omit(node.data, {
          keys: ['__selected'],
        })),
        __index: index,
      });
    });

    return compiled;
  }

  get groups(): FieldmagicDataGridRowNode[] {
    this._lib.stopEditing();

    const compiled: FieldmagicDataGridRowNode[] = [];

    this._lib.forEachNode((node) => {
      if (!node.group) {
        return;
      }

      compiled.push(node);
    });

    return compiled;
  }

  get selections(): _IndexedData<T>[] {
    this._lib.stopEditing();

    const compiled: _IndexedData<T>[] = [];

    this._lib.forEachNode((node, index) => {
      if (blank(node.data) || !node.isSelected()) {
        return;
      }

      compiled.push({
        ...(<T>data_omit(node.data, {
          keys: ['__selected'],
        })),
        __index: index,
      });
    });

    return compiled;
  }

  constructor(
    private readonly _lib: GridApi,
    private readonly _notifications: NotificationService,
    private readonly _states: DataStateStore,
    private readonly _props: _FieldmagicDataGridApiData,
  ) {}

  addItems(
    data: T | T[],
    opts: {
      after?: RowNode;
      before?: RowNode;
    } = {},
  ): void {
    let index: number;

    if (filled(opts.after)) {
      index = opts.after.rowIndex;

      if (filled(opts.after.parent)) {
        index = this._identifySourceIndexFromGrid(opts.after.data);
      }

      index += 1;
    } else if (filled(opts.before)) {
      index = opts.before.rowIndex;

      if (filled(opts.before.parent)) {
        index = this._identifySourceIndexFromGrid(opts.before.data);
      }
    }

    this._onTransact(
      this._lib.applyTransaction({
        add: arr_wrap(data),
        addIndex: index,
      }),
    );
  }

  updateItems = (data: T | T[]) =>
    this._onTransact(
      this._lib.applyTransaction({
        update: arr_wrap(data),
      }),
    );

  removeItems = (data: T | T[]) =>
    defer(() => {
      data = arr_wrap(data);

      const persisted = arr_filter(data, {
        predicate: (data: T & FieldmagicDataGridPersistedData) =>
          data_is_true(data.__persisted),
      });

      if (filled(persisted)) {
        return this._notifications
          .sendConfirmation('Are you want to delete this item?')
          .pipe(
            filter((response) => data_is_true(response.answer)),
            map(() => data),
          );
      }

      return of(data);
    }).pipe(
      take(1),
      tap(() =>
        this._onTransact(
          this._lib.applyTransaction({
            remove: arr_wrap(data),
          }),
        ),
      ),
    );

  setDatasource(dataSource: IDatasource) {
    this._lib.setDatasource(dataSource);
  }

  purgeInfiniteCache() {
    this._lib.purgeInfiniteCache();
  }

  goToPage(pageNum: number) {
    this._lib.paginationGoToPage(pageNum + 1);
  }

  fitColumnsToTable() {
    this._lib.sizeColumnsToFit();
  }

  savable(): _IndexedData<T>[] {
    this.destroyState();

    return this.data;
  }

  storeState(): void {
    const key = this._props.data_state_key;

    if (blank(key)) {
      return;
    }

    const compiled: _IndexedData<T>[] = [];

    this._lib.forEachNode((node, index) => {
      if (blank(node.data)) {
        return;
      }

      compiled.push({
        ...(<T>data_omit(node.data, {
          keys: ['__selected'],
        })),
        __index: index,
      });
    });

    this._states.update(key, compiled);
  }

  destroyState(): void {
    const key = this._props.data_state_key;

    if (blank(key)) {
      return;
    }

    this._states.destroy(key);
  }

  private _identifySourceIndexFromGrid(data: T): number {
    const compiled: string[] = [];

    this._lib.forEachNode((node) => {
      if (blank(node.data)) {
        return;
      }

      compiled.push(node.data.id);
    });

    const dIndex = Object.keys(compiled).length;

    if (blank(data)) {
      return dIndex;
    }

    const cIndex = compiled.findIndex((id) => data.id === id);

    if (cIndex < 0) {
      return dIndex;
    }

    return cIndex;
  }

  private _onTransact(transaction: RowNodeTransaction): void {
    const nodes = [
      ...transaction.add,
      ...transaction.update,
      ...transaction.remove,
    ];

    const refreshed: Record<string, RowNode> = {};

    for (const node of nodes) {
      if (blank(node.parent) || filled(data_get(refreshed, [node.parent.id]))) {
        continue;
      }

      refreshed[node.parent.id] = node.parent;
    }

    this.storeState();

    if (blank(refreshed)) {
      return;
    }

    this._lib.refreshCells({
      rowNodes: Object.values(refreshed),
      columns: ['ag-Grid-AutoColumn'],
      force: true,
    });
  }
}

export type FieldmagicDataGridColumnType =
  | 'text'
  | 'numeric'
  | 'currency'
  | 'percentage'
  | 'progressive_percentage'
  | 'dropdown'
  | 'relate'
  | 'checkbox'
  | 'datetime'
  | 'textarea';

export type FieldmagicDataGridAggregator<T extends Stringable> = (
  values: T[],
  params: IAggFuncParams,
) => T;
export type FieldmagicDataGridSerializer<T = any> = (
  value: OptionalValue<T>,
  params: ValueSetterParams,
) => {
  success: boolean;
  updates?: Record<string, any>;
};
export type FieldmagicDataGridDeserializer<R = any> = (
  params: ValueGetterParams,
) => R;
export type FieldmagicDataGridFormatter<R = any, T = any> = (
  value: OptionalValue<T>,
  params: ValueFormatterParams,
) => R;

export type FieldmagicDataGridGroupIdGenerator<R = any, T = any> = (
  value: OptionalValue<T>,
  params: _DataGridGroupIdGeneratorParams<T>,
) => R;

export type FieldmagicDataGridColumnDef = {
  label?: string;
  field_type?: FieldmagicDataGridColumnType;
  field_name?: string;
  grouped?: boolean;
  enable_grouping?: boolean;
  auto_grouped?: boolean;
  hide_when_grouped_column_collapsed?: boolean;
  pinned?: _DataGridPinned;
  editable?: boolean;
  editable_predicate?: (params: IsColumnFuncParams) => boolean;
  sortable?: boolean;
  filterable?: boolean;
  hidden?: boolean;
  aggregation?: string | FieldmagicDataGridAggregator<Stringable>;
  // @see: https://www.ag-grid.com/archive/24.1.0/javascript-grid-aggregation/
  enabled_aggregations?: string[];
  children?: FieldmagicDataGridColumnDef[];

  /// checkbox
  enable_checkbox?:
    | boolean
    | ((
        data: OptionalValue<Record<string, any>>,
        params: Record<string, any>,
      ) => boolean);

  // make sure that functions here are binded
  // to the current hosting component by using `.bind`
  serializer?: FieldmagicDataGridSerializer;
  deserializer?:
    | FieldmagicDataGridDeserializer
    | FieldmagicDataGridCommonDeserializer;
  group_id?: FieldmagicDataGridGroupIdGenerator;
  formatter?: FieldmagicDataGridFormatter;

  /// input config
  /// see corresponding rich cell editor params
  input_config?:
    | FieldmagicDataGridInputConfig
    | ((params: ICellEditorParams) => FieldmagicDataGridInputConfig);

  /// rendering config
  rendering_config?: FieldmagicDataGridRenderingConfig;

  /// actions
  on_double_clicked?: (event: CellDoubleClickedEvent) => void;

  // use only when overriding ag-grid library options
  // that is not supported by the current api wrapper
  lib_column_def?: ColDef;

  /// used as data reference when computing number of grouped items.
  /// Useful when prepopulating set of groups without lines
  is_group_child_count_ref?: boolean;
};

export type FieldmagicDataGridOptions = {
  enable_ui_grouping?: boolean;
  disable_aggregates_on_header?: boolean;
  enable_row_selection?: 'multi' | 'single' | boolean;
  enable_charts?: boolean;
  enable_range_selection?: boolean;
  enable_totals?: boolean;
  row_model_type?: 'clientSide' | 'infinite';
  pagination?: boolean;
  pagination_page_size?: number;
  cache_block_size?: number;
  enable_configuring_columns?: boolean;
};

export type FieldmagicDataGridContextMenu = {
  label: string;
  /// please make sure that functions here are binded to host component
  action: (params: FieldmagicDataGridContextMenuParams) => void;
  /// please make sure that functions here are binded to host component
  predicate?: (params: FieldmagicDataGridContextMenuParams) => boolean;

  is_on_empty_node?: boolean;
  is_for_group_action?: boolean;
  is_row_action?: boolean;
};

export type FieldmagicDataGridCommonDeserializer = {
  multiply?: {
    multiplicant_ref: string;
    multiplier_ref: string;
  };
};

export type FieldmagicDataGridOnReadyEvent<D extends Identifiable> = {
  api: FieldmagicDataGridApi<D>;
};

export type FieldmagicDataGridRowNode = RowNode;
export type FieldmagicDataGridContextMenuParams = GetContextMenuItemsParams;
export type FieldmagicDataGridSize = 'full' | 'lg' | 'md';

export type FieldmagicDataGridSelectableData = {
  __selected?: boolean;
};

export type FieldmagicDataGridOnDoubleClickedEvent = CellDoubleClickedEvent;

export type FieldmagicDataGridPersistedData = {
  __persisted?: boolean;
};

type _DataGridPinned = 'left' | 'right' | 'unpinned';

type _DataGridGroupIdGeneratorParams<T = any> = {
  value: T;
};

type _IndexedData<T extends Identifiable> = T & {
  __index: number;
};

const _COLUMN_TYPE_RENDERER_COMPONENTS = {
  currency: FieldmagicDataGridCurrencyCellRenderer,
  numeric: FieldmagicDataGridNumericCellRenderer,
  percentage: FieldmagicDataGridPercentageCellRenderer,
  progressive_percentage: FieldmagicDataGridProgressivePercentageCellRenderer,
  checkbox: FieldmagicDataGridCheckboxCellRenderer,
  datetime: FieldmagicDataGridDateTimeCellRenderer,
  text: FieldmagicDataGridTextCellRendererComponent,
  __grouping: FieldmagicDataGridGroupCellRendererComponent,
  '__sidebar:menu': FieldmagicDataGridSidebarMenuToolPanelComponent,
};

const _COLUMN_TYPE_INPUT_COMPONENTS = {
  'input:dropdown': FieldmagicDataGridDropdownCellInputComponent,
  'input:relate': FieldmagicDataGridRelateDropdownCellInputComponent,
  'input:checkbox': FieldmagicDataGridCheckboxCellInputComponent,
  'input:datetime': FieldmagicDataGridDateTimeCellInputComponent,
  'input:textarea': FieldmagicDataGridTextareaCellInputComponent,
};

const _CUSTOM_AGGREGATES = {
  checkbox_aggregate: (params: IAggFuncParams) => {
    const truths = arr_filter(params.values, {
      predicate: (value) => data_is_true(value),
    });

    return arr_count(params.values) === arr_count(truths);
  },
};

type _FieldmagicDataGridApiData = {
  data_state_key?: string;
};
